import React, { useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import MinimizeIcon from '@mui/icons-material/Minimize';
import { useNavigate } from 'react-router-dom';
import { DOCK_CSS } from '../../../components/layout/floatingDockLayout';
import { getApiUrl } from '../../../config/environment';
import type { TeamChatConversation, TeamChatMessage } from '../../../services/teamChatService';
import TeamChatAvatar from '../components/TeamChatAvatar';
import TeamChatComposer from '../components/TeamChatComposer';
import TeamChatMessageBubble from '../components/TeamChatMessageBubble';
import {
  TEAM_CHAT_DOCK_WINDOW_STEP,
  TEAM_CHAT_DOCK_WINDOW_WIDTH,
  getTeamChatDockZ,
  TEAM_CHAT_THEME,
  teamChatDayDividerSx,
  teamChatMessageRowSx,
} from '../teamChatTheme';
import {
  firstName,
  formatDayDivider,
  isGroupedWithPrevious,
  needsDayDivider,
  parseDate,
  resolveAttachmentUrl,
} from '../teamChatUtils';
import type { useTeamChatDockController } from './useTeamChatDockController';

interface TeamChatFloatingWindowProps {
  conversation: TeamChatConversation;
  controller: ReturnType<typeof useTeamChatDockController>;
  position: number;
  mobile?: boolean;
  bottomOffset?: number;
}

const TeamChatFloatingWindow: React.FC<TeamChatFloatingWindowProps> = ({
  conversation,
  controller,
  position,
  mobile = false,
  bottomOffset = 16,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dockZ = getTeamChatDockZ(theme);
  const endRef = useRef<HTMLDivElement | null>(null);
  const thread = controller.threads[conversation.id];
  const messages = thread?.messages || [];
  const apiBase = useMemo(() => getApiUrl(), []);
  const counterpart = conversation.counterpart;
  const online = counterpart ? controller.onlineUserIds.has(counterpart.user_id) : false;

  useEffect(() => {
    requestAnimationFrame(() => endRef.current?.scrollIntoView({ block: 'end' }));
  }, [messages.length]);

  const subtitle = useMemo(() => {
    if (conversation.type === 'group') {
      const onlineCount = conversation.participants.filter((participant) =>
        controller.onlineUserIds.has(participant.user_id),
      ).length;
      return `${conversation.participant_count} participantes · ${onlineCount} online`;
    }
    return online ? 'Online' : 'Offline';
  }, [conversation, controller.onlineUserIds, online]);

  const typingLabel = useMemo(() => {
    const names = Object.values(thread?.typingUsers || {}).map(firstName);
    if (names.length === 0) return '';
    if (names.length === 1) return `${names[0]} está digitando…`;
    return `${names.slice(0, 2).join(' e ')} estão digitando…`;
  }, [thread?.typingUsers]);

  const isReadByAll = (message: TeamChatMessage) => {
    const others = conversation.participants.filter(
      (participant) => participant.user_id !== controller.currentUserId,
    );
    if (!others.length) return false;
    const sentAt = parseDate(message.created_at)?.getTime() ?? 0;
    return others.every((participant) => {
      const readAt = parseDate(participant.last_read_at)?.getTime() ?? 0;
      return readAt >= sentAt;
    });
  };

  return (
    <Paper
      data-team-chat-surface="true"
      elevation={0}
      onMouseDown={() => controller.setFocusedId(conversation.id)}
      sx={{
        position: 'fixed',
        zIndex:
          dockZ.window +
          (controller.focusedId === conversation.id ? dockZ.windowFocused - dockZ.window : 0),
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: '#fff',
        border: `1px solid ${TEAM_CHAT_THEME.border}`,
        boxShadow: '0 18px 48px rgba(15, 23, 42, 0.22)',
        ...(mobile
          ? {
              inset: 0,
              width: '100vw',
              height: '100dvh',
              borderRadius: 0,
            }
          : {
              right: `calc(env(safe-area-inset-right, 0px) + ${DOCK_CSS.teamChatCardsRight} + ${position * TEAM_CHAT_DOCK_WINDOW_STEP}px)`,
              bottom: `calc(env(safe-area-inset-bottom, 0px) + ${bottomOffset}px)`,
              width: { sm: 300, md: TEAM_CHAT_DOCK_WINDOW_WIDTH },
              maxWidth: 'calc(100vw - 24px)',
              height: { xs: 'min(480px, calc(100dvh - 32px))', md: 'min(520px, calc(100dvh - 40px))' },
              borderRadius: '16px 16px 10px 10px',
            }),
      }}
    >
      <Box
        sx={{
          minHeight: mobile ? 64 : 58,
          px: 1.5,
          pt: mobile ? 'max(10px, env(safe-area-inset-top, 0px))' : 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          bgcolor: '#fff',
          borderBottom: `1px solid ${TEAM_CHAT_THEME.borderSoft}`,
        }}
      >
        <TeamChatAvatar
          name={conversation.title}
          avatarUrl={conversation.avatar_url}
          gender={counterpart?.gender}
          isGroup={conversation.type === 'group'}
          online={conversation.type === 'direct' ? online : undefined}
          size={36}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontSize: '0.86rem', fontWeight: 700, color: TEAM_CHAT_THEME.navy }}
          >
            {conversation.title}
          </Typography>
          <Typography
            noWrap
            sx={{
              fontSize: '0.68rem',
              fontWeight: typingLabel ? 600 : 500,
              color: typingLabel ? TEAM_CHAT_THEME.tealDark : TEAM_CHAT_THEME.slate,
            }}
          >
            {typingLabel || subtitle}
          </Typography>
        </Box>
        <Tooltip title="Abrir página completa">
          <IconButton size="small" onClick={() => navigate('/team-chat')}>
            <LaunchIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        {!mobile && (
          <Tooltip title="Minimizar">
            <IconButton size="small" onClick={() => controller.minimizeConversation(conversation.id)}>
              <MinimizeIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Fechar">
          <IconButton size="small" onClick={() => controller.closeConversation(conversation.id)}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          px: { xs: 0.85, sm: 1, md: 1.15 },
          py: { xs: 0.85, sm: 1.05, md: 1.25 },
          bgcolor: TEAM_CHAT_THEME.threadBg,
          backgroundImage: `linear-gradient(${TEAM_CHAT_THEME.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${TEAM_CHAT_THEME.gridLine} 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
          '&::-webkit-scrollbar': { width: 5 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(15,23,42,.18)', borderRadius: 99 },
        }}
      >
        {thread?.hasMore && (
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Button
              size="small"
              disabled={thread.loadingOlder}
              onClick={() => void controller.loadOlder(conversation.id)}
              sx={{ textTransform: 'none', fontSize: '0.72rem' }}
            >
              {thread.loadingOlder ? 'Carregando…' : 'Carregar mensagens anteriores'}
            </Button>
          </Box>
        )}

        {thread?.loading ? (
          <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.8rem', color: TEAM_CHAT_THEME.slate }}>
              Envie uma mensagem para começar a conversa.
            </Typography>
          </Box>
        ) : (
          messages.map((message, index) => {
            const previous = messages[index - 1];
            const mine = message.sender_id === controller.currentUserId;
            const grouped = isGroupedWithPrevious(message, previous);
            return (
              <React.Fragment key={message.id}>
                {needsDayDivider(message, previous) && (
                  <Box sx={teamChatDayDividerSx}>{formatDayDivider(message.created_at)}</Box>
                )}
                <Box
                  className="team-chat-message-row"
                  sx={{
                    ...teamChatMessageRowSx(mine),
                    mb: grouped ? 0.15 : 0.5,
                    '& .team-chat-actions': { display: mobile ? 'flex' : undefined },
                  }}
                >
                  <TeamChatMessageBubble
                    message={message}
                    mine={mine}
                    showAuthor={conversation.type === 'group' && !mine && !grouped}
                    readByAll={isReadByAll(message)}
                    currentUserId={controller.currentUserId}
                    attachmentUrl={resolveAttachmentUrl(message.attachment_url, apiBase)}
                    onReply={(item) => controller.setReplyTo(conversation.id, item)}
                    onEdit={(item) => controller.setEditing(conversation.id, item)}
                    onDelete={(item) => void controller.deleteMessage(conversation.id, item)}
                    onToggleReaction={(item, emoji) =>
                      void controller.toggleReaction(conversation.id, item, emoji)
                    }
                  />
                </Box>
              </React.Fragment>
            );
          })
        )}
        <div ref={endRef} />
      </Box>

      <Box sx={{ flexShrink: 0, pb: mobile ? 'env(safe-area-inset-bottom, 0px)' : 0 }}>
        <TeamChatComposer
          disabled={!controller.connected}
          sending={thread?.sending || false}
          replyTo={thread?.replyTo || null}
          editing={thread?.editing || null}
          onCancelReply={() => controller.setReplyTo(conversation.id, null)}
          onCancelEdit={() => controller.setEditing(conversation.id, null)}
          onSend={(body) => void controller.send(conversation.id, body)}
          onSendFile={(file) => void controller.sendFile(conversation.id, file)}
          onSubmitEdit={(body) => void controller.submitEdit(conversation.id, body)}
          onTyping={(isTyping) => controller.emitTyping(conversation.id, isTyping)}
          onError={controller.showError}
        />
      </Box>
    </Paper>
  );
};

export default TeamChatFloatingWindow;
